(function () {
  if (customElements.get('inventory-grid')) return;

  // Metafield sorts can't be done by Shopify natively, so they ride a separate
  // ?in_sort= param resolved client-side. Maps a sort key to the card data-*
  // attribute it reads and the direction (1 = ascending, -1 = descending).
  var META_SORTS = {
    'year-desc': { attr: 'year', dir: -1 },
    'year-asc': { attr: 'year', dir: 1 },
    'length-desc': { attr: 'length', dir: -1 },
    'length-asc': { attr: 'length', dir: 1 },
    'hours-asc': { attr: 'hours', dir: 1 },
    'hours-desc': { attr: 'hours', dir: -1 },
  };

  var IN_SORT_PARAM = 'in_sort';

  class InventoryGrid extends HTMLElement {
    constructor() {
      super();
      this.onSortChange = this.onSortChange.bind(this);
      this.onSearchSubmit = this.onSearchSubmit.bind(this);
      this.onPagerClick = this.onPagerClick.bind(this);
      // Holds the full, sorted list of card nodes while a metafield sort is
      // active so page changes are in-memory (no refetch).
      this.sortedCards = null;
    }

    connectedCallback() {
      this.sortSelect = this.querySelector('[data-in-sort]');
      this.searchForm = this.querySelector('[data-in-search]');
      this.searchInput = this.querySelector('[data-in-search-input]');
      this.list = this.querySelector('[data-in-list]');
      this.status = this.querySelector('[data-in-status]');

      if (this.sortSelect) this.sortSelect.addEventListener('change', this.onSortChange);
      if (this.searchForm) this.searchForm.addEventListener('submit', this.onSearchSubmit);

      var inSort = getParam(window.location.href, IN_SORT_PARAM);

      // Keep the active metafield sort alive across server-side filter submits:
      // the filters form rebuilds the query string from its fields, so inject a
      // hidden in_sort input (Liquid can't render it — it can't read the param).
      this.syncFilterFormSort(inSort);

      if (inSort && META_SORTS[inSort]) {
        // Reflect the active sort in the dropdown (Liquid couldn't).
        if (this.sortSelect) this.sortSelect.value = inSort;
        // Filter checkboxes ride the form (hidden input above), but location
        // pills + facet links are anchors with Shopify-built URLs that omit
        // in_sort — rewrite them so the sort survives a pill/facet click.
        this.persistSortOnLinks(inSort);
        this.applyMetafieldSort(inSort);
      } else {
        // No valid metafield sort: reveal the native-order list in case the
        // pre-paint guard optimistically hid it (e.g. a stale/invalid in_sort).
        this.showLoading(false);
      }
    }

    disconnectedCallback() {
      if (this.sortSelect) this.sortSelect.removeEventListener('change', this.onSortChange);
      if (this.searchForm) this.searchForm.removeEventListener('submit', this.onSearchSubmit);
    }

    onSortChange() {
      var value = this.sortSelect && this.sortSelect.value;
      if (!value) return;

      var url = new URL(window.location.href);
      url.searchParams.delete('page');

      if (META_SORTS[value]) {
        // Metafield sort: drop the native sort_by, carry our own param, reload.
        // On reload connectedCallback() picks it up and reorders client-side.
        url.searchParams.set(IN_SORT_PARAM, value);
        url.searchParams.delete('sort_by');
      } else {
        // Native Shopify sort: server-side. Clear any stale metafield sort.
        url.searchParams.set('sort_by', value);
        url.searchParams.delete(IN_SORT_PARAM);
      }

      // Land on the results region after reload, exactly like the filters form.
      url.hash = 'inventory-results';

      window.location.assign(url.toString());
    }

    onSearchSubmit(event) {
      event.preventDefault();

      var userQ = (this.searchInput && this.searchInput.value ? this.searchInput.value : '').trim();
      var handle = this.dataset.inventoryHandle || 'inventory';

      if (!userQ) {
        var home = new URL('/collections/' + handle, window.location.origin);
        forwardFilterParams(window.location.href, home);
        forwardSort(window.location.href, home);
        window.location.assign(home.toString());
        return;
      }

      var target = new URL('/search', window.location.origin);
      var scopeQuery = (this.dataset.inventoryScopeQuery || '').trim();
      var scopedQ = scopeQuery ? scopeQuery + ' ' + userQ : userQ;
      target.searchParams.set('q', scopedQ);
      target.searchParams.set('type', 'product');
      target.searchParams.set('view', 'inventory');
      target.searchParams.set('options[prefix]', 'last');
      target.searchParams.set('options[unavailable_products]', 'last');

      forwardFilterParams(window.location.href, target);
      forwardSort(window.location.href, target);

      window.location.assign(target.toString());
    }

    // ---- Metafield sort (client-side) -------------------------------------

    // Fetch every page of the current filtered set via the Section Rendering
    // API (in parallel), collect the rendered cards, sort them by the chosen
    // metafield attribute, then paginate client-side.
    applyMetafieldSort(sortKey) {
      var spec = META_SORTS[sortKey];
      if (!spec || !this.list) {
        this.showLoading(false); // can't sort — reveal native order
        return;
      }

      var sectionId = this.resolveSectionId();
      if (!sectionId) {
        this.showLoading(false); // Section Rendering API unavailable; leave native order
        return;
      }

      var total = parseInt(this.dataset.totalCount, 10) || 0;
      this.perPage = parseInt(this.dataset.perPage, 10) || 16;
      var pages = Math.max(1, Math.ceil(total / this.perPage));

      this.showLoading(true);

      var requests = [];
      for (var p = 1; p <= pages; p++) {
        requests.push(this.fetchPageCards(sectionId, p));
      }

      var self = this;
      Promise.all(requests)
        .then(function (pages) {
          var all = [];
          for (var i = 0; i < pages.length; i++) {
            all = all.concat(pages[i].cards);
          }
          all.sort(function (a, b) {
            return compareCards(a, b, spec.attr, spec.dir);
          });
          self.sortedCards = all;

          // Capture promo cards (if any) from the first page — they're identical
          // across pages — so renderClientPage can re-inject them per page,
          // mirroring the native snippet. No promo block → empty array → no-op.
          self.promos = (pages[0] ? pages[0].promos : []).map(function (n) {
            return { position: parseInt(n.dataset.promoPosition, 10) || 0, node: n };
          });

          var startPage = parseInt(getParam(window.location.href, 'page'), 10) || 1;
          self.renderClientPage(startPage, false);
        })
        .catch(function () {
          // Network/parse failure: leave the server-rendered native order.
          self.sortedCards = null;
        })
        .then(function () {
          self.showLoading(false);
        });
    }

    // One Section Rendering API request → { cards, promos } node arrays.
    fetchPageCards(sectionId, page) {
      var url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', String(page));
      url.searchParams.delete(IN_SORT_PARAM); // server renders native order; we re-sort anyway

      return fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (res) {
          if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          return {
            cards: Array.prototype.slice.call(doc.querySelectorAll('[data-in-card]')),
            promos: Array.prototype.slice.call(doc.querySelectorAll('[data-in-promo]')),
          };
        });
    }

    // Render one client-side page from this.sortedCards into the list, then
    // (re)build the pager. updateHistory pushes ?page= without reloading.
    renderClientPage(page, updateHistory) {
      if (!this.sortedCards || !this.list) return;

      var perPage = this.perPage || 16;
      var totalPages = Math.max(1, Math.ceil(this.sortedCards.length / perPage));
      page = Math.min(Math.max(1, page), totalPages);

      var start = (page - 1) * perPage;
      var slice = this.sortedCards.slice(start, start + perPage);

      var promos = this.promos || [];
      this.list.innerHTML = '';
      for (var i = 0; i < slice.length; i++) {
        // Re-inject any promo configured before the (i+1)-th boat on this page,
        // per-page like the native snippet. No-op when there are no promos.
        for (var j = 0; j < promos.length; j++) {
          if (promos[j].position === i + 1) {
            this.list.appendChild(promos[j].node.cloneNode(true));
          }
        }
        // Clone so the master sorted array keeps intact nodes across re-renders.
        this.list.appendChild(slice[i].cloneNode(true));
      }

      // Match the native snippet: count line + pager only when there's more
      // than one page. With a single page, render neither (and drop any count
      // line left over from the server render).
      if (totalPages > 1) {
        this.updateCount(start + slice.length, this.sortedCards.length);
      } else {
        var leftoverCount = this.querySelector('.in-grid__count');
        if (leftoverCount) leftoverCount.remove();
      }
      this.renderPager(page, totalPages);

      if (updateHistory) {
        var url = new URL(window.location.href);
        if (page > 1) url.searchParams.set('page', String(page));
        else url.searchParams.delete('page');
        window.history.replaceState({}, '', url.toString());
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // The clicked pager link was destroyed by the rebuild above, so focus
        // fell to <body>. Restore it to the new current-page marker (WCAG 2.4.3).
        var current = this.querySelector('.in-grid__page--current');
        if (current) current.focus();
      }
    }

    // Mirror the markup of snippets/inventory-grid-results.liquid so the
    // client pager is visually identical to the native one.
    renderPager(page, totalPages) {
      var existing = this.querySelector('.in-grid__pagination');
      if (existing) existing.remove();
      if (totalPages <= 1) return;

      var nav = document.createElement('nav');
      nav.className = 'in-grid__pagination';
      nav.setAttribute('aria-label', 'Pagination');
      nav.setAttribute('data-in-client-pager', '');

      if (page > 1) {
        nav.appendChild(this.pagerArrow('prev', page - 1));
      }

      var ul = document.createElement('ul');
      ul.className = 'in-grid__pages';
      ul.setAttribute('role', 'list');

      // Mirror Shopify's native windowing: always show page 1, the last page,
      // and every page within ±2 of the current page. Insert a single ellipsis
      // gap wherever consecutive shown pages aren't adjacent.
      var shown = [];
      for (var n = 1; n <= totalPages; n++) {
        if (n === 1 || n === totalPages || Math.abs(n - page) <= 2) {
          shown.push(n);
        }
      }

      var prev = 0;
      for (var i = 0; i < shown.length; i++) {
        var num = shown[i];
        if (num - prev > 1) {
          ul.appendChild(this.pagerGap());
        }
        ul.appendChild(this.pagerPage(num, num === page));
        prev = num;
      }
      nav.appendChild(ul);

      if (page < totalPages) {
        nav.appendChild(this.pagerArrow('next', page + 1));
      }

      nav.addEventListener('click', this.onPagerClick);
      this.appendChild(nav);
    }

    // One <li> page item: the current page is a non-clickable span, every other
    // page is a clickable link carrying data-in-page for onPagerClick.
    pagerPage(n, isCurrent) {
      var li = document.createElement('li');
      if (isCurrent) {
        var cur = document.createElement('span');
        cur.className = 'in-grid__page in-grid__page--current';
        cur.setAttribute('aria-current', 'page');
        // Programmatically focusable so renderClientPage can restore focus here
        // after the clicked link is destroyed by the pager rebuild (WCAG 2.4.3).
        cur.setAttribute('tabindex', '-1');
        cur.textContent = String(n);
        li.appendChild(cur);
      } else {
        var a = document.createElement('a');
        a.className = 'in-grid__page';
        a.href = '#inventory-results';
        a.textContent = String(n);
        a.setAttribute('data-in-page', String(n));
        li.appendChild(a);
      }
      return li;
    }

    // One <li> ellipsis gap, matching the native snippet's gap markup.
    pagerGap() {
      var li = document.createElement('li');
      var span = document.createElement('span');
      span.className = 'in-grid__page in-grid__page--gap';
      span.setAttribute('aria-hidden', 'true');
      span.textContent = '…';
      li.appendChild(span);
      return li;
    }

    pagerArrow(kind, targetPage) {
      var a = document.createElement('a');
      a.className = 'in-grid__page in-grid__page--' + kind;
      a.href = '#inventory-results';
      a.setAttribute('data-in-page', String(targetPage));
      a.setAttribute('aria-label', kind === 'prev' ? 'Previous' : 'Next');
      // SVG mirrors the native arrows.
      var d = kind === 'prev' ? 'M12 4l-6 6 6 6' : 'M8 4l6 6-6 6';
      a.innerHTML =
        '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="' +
        d +
        '" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      return a;
    }

    onPagerClick(event) {
      var link = event.target.closest('[data-in-page]');
      if (!link) return;
      event.preventDefault();
      var page = parseInt(link.getAttribute('data-in-page'), 10);
      if (page) this.renderClientPage(page, true);
    }

    updateCount(shown, total) {
      var existing = this.querySelector('.in-grid__count');
      var text = 'Showing ' + shown + ' of ' + total + ' boats';
      if (existing) {
        existing.textContent = text;
        return;
      }
      var p = document.createElement('p');
      p.className = 'in-grid__count';
      p.setAttribute('aria-live', 'polite');
      p.textContent = text;
      if (this.list && this.list.parentNode) {
        this.list.parentNode.insertBefore(p, this.list.nextSibling);
      }
    }

    // Toggle the neutral loading state: .is-presorting hides the native-order
    // list and shows the placeholder grid (see section CSS). No overlay, no
    // dimmed stale cards, no reorder jump — it reads like a normal sort load.
    showLoading(on) {
      this.classList.toggle('is-presorting', on);
      this.setAttribute('aria-busy', on ? 'true' : 'false');
      if (this.status) {
        this.status.textContent = on ? this.dataset.sortingLabel || 'Sorting boats…' : '';
      }
    }

    // The grid is rendered inside <div id="shopify-section-{sectionId}">.
    resolveSectionId() {
      var wrapper = this.closest('.shopify-section');
      if (wrapper && wrapper.id) {
        return wrapper.id.replace(/^shopify-section-/, '');
      }
      return null;
    }

    // Append ?in_sort= to filter anchor links (location pills + facet
    // add/remove links) so a metafield sort persists when they're clicked.
    // The "Clear all" link is intentionally left alone — it resets everything,
    // sort included.
    persistSortOnLinks(inSort) {
      var anchors = Array.prototype.slice.call(this.querySelectorAll('.in-pill'));
      var filters = document.querySelector('inventory-filters');
      if (filters) {
        var facetLinks = filters.querySelectorAll(
          'a[href*="filter."], a[href*="#inventory-results"]',
        );
        anchors = anchors.concat(Array.prototype.slice.call(facetLinks));
      }
      anchors.forEach(function (a) {
        if (a.classList.contains('in-filters__clear')) return;
        try {
          var u = new URL(a.href, window.location.origin);
          u.searchParams.set(IN_SORT_PARAM, inSort);
          a.href = u.toString();
        } catch (e) {
          /* leave malformed hrefs untouched */
        }
      });
    }

    syncFilterFormSort(inSort) {
      var form = document.querySelector('form[data-in-body]');
      if (!form) return;
      var input = form.querySelector('input[name="' + IN_SORT_PARAM + '"]');
      if (inSort && META_SORTS[inSort]) {
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = IN_SORT_PARAM;
          form.appendChild(input);
        }
        input.value = inSort;
      } else if (input) {
        input.parentNode.removeChild(input);
      }
    }
  }

  // ---- helpers ------------------------------------------------------------

  function getParam(href, name) {
    return new URL(href).searchParams.get(name);
  }

  function forwardFilterParams(fromUrl, toUrl) {
    var from = new URL(fromUrl).searchParams;
    from.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) {
        toUrl.searchParams.append(key, value);
      }
    });
  }

  // Forward both the native sort_by and our metafield in_sort param.
  function forwardSort(fromUrl, toUrl) {
    var params = new URL(fromUrl).searchParams;
    var sort = params.get('sort_by');
    if (sort) toUrl.searchParams.set('sort_by', sort);
    var inSort = params.get(IN_SORT_PARAM);
    if (inSort) toUrl.searchParams.set(IN_SORT_PARAM, inSort);
  }

  // Sort comparator. Missing/blank values always sink to the end regardless
  // of direction so "no data" boats never lead the list.
  function compareCards(a, b, attr, dir) {
    var av = num(a.dataset[attr]);
    var bv = num(b.dataset[attr]);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return (av - bv) * dir;
  }

  function num(raw) {
    if (raw == null || raw === '') return null;
    var n = parseFloat(raw);
    return isNaN(n) ? null : n;
  }

  customElements.define('inventory-grid', InventoryGrid);
})();
